import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Box,
  useMediaQuery,
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, EmojiObjects } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  alertBanner: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,193,7,0.1)" : "#fff8e1",
    color: theme.palette.type === "dark" ? "#ffd54f" : "#7a5e00",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,193,7,0.2)" : "#ffe082"}`,
    fontSize: "0.875rem",
    "& a": {
      color: theme.palette.primary.main,
      fontWeight: "bold",
      textDecoration: "underline",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1.5),
    opacity: 0.35,
  },
}));

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];
    prompts.forEach((prompt) => {
      const idx = state.findIndex((p) => p.id === prompt.id);
      if (idx !== -1) state[idx] = prompt;
      else newPrompts.push(prompt);
    });
    return [...state, ...newPrompts];
  }
  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const idx = state.findIndex((p) => p.id === prompt.id);
    if (idx !== -1) { state[idx] = prompt; return [...state]; }
    return [prompt, ...state];
  }
  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const idx = state.findIndex((p) => p.id === promptId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Prompts = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("prompt", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    });

    return () => socket.disconnect();
  }, [user]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  const renderActions = (prompt) => (
    <>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => handleEditPrompt(prompt)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          onClick={() => {
            setSelectedPrompt(prompt);
            setConfirmModalOpen(true);
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <EmojiObjects className={classes.emptyIcon} />
      <Typography variant="body2" gutterBottom>
        Nenhum prompt cadastrado ainda.
      </Typography>
      <Button
        size="small"
        color="primary"
        variant="outlined"
        onClick={handleOpenPromptModal}
        style={{ marginTop: 8 }}
      >
        Adicionar primeiro prompt
      </Button>
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />
      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPromptModal}
          >
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box className={classes.alertBanner}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          <strong>Aviso Importante:</strong> Caso você esteja enfrentando interrupções no uso do OpenAI com o Message Express, saiba que isso não é um problema no sistema. O OpenAI oferece um crédito inicial gratuito de US$ 5 para novos usuários, válido por aproximadamente 3 meses. Após esse período ou ao atingir o limite do crédito, o serviço é suspenso automaticamente. Para continuar utilizando, é necessário adicionar créditos à sua conta. Verifique seu saldo em{' '}
          <a href="https://platform.openai.com/usage" target="_blank" rel="noopener noreferrer">
            Uso
          </a>{', '}
          recarregue em{' '}
          <a href="https://platform.openai.com/account/billing/overview" target="_blank" rel="noopener noreferrer">
            Fatura
          </a>{' '}
          e confira sua chave em{' '}
          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
            API Keys
          </a>.
        </Typography>
      </Box>

      <Paper className={classes.mainPaper} variant="outlined">
        {!loading && prompts.length === 0 && emptyState}

        {isMobile ? (
          (prompts.length > 0 || loading) && (
            <div>
              {prompts.map((prompt) => (
                <MobileRecordCard
                  key={prompt.id}
                  title={prompt.name}
                  subtitle={prompt.queue?.name}
                  headerAction={renderActions(prompt)}
                  rows={[
                    { label: i18n.t("prompts.table.max_tokens"), value: prompt.maxTokens },
                  ]}
                />
              ))}
              {loading &&
                [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
            </div>
          )
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">{i18n.t("prompts.table.name")}</TableCell>
                <TableCell align="left">{i18n.t("prompts.table.queue")}</TableCell>
                <TableCell align="left">{i18n.t("prompts.table.max_tokens")}</TableCell>
                <TableCell align="center">{i18n.t("prompts.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.map((prompt) => (
                <TableRow key={prompt.id} hover>
                  <TableCell align="left">{prompt.name}</TableCell>
                  <TableCell align="left">{prompt.queue?.name}</TableCell>
                  <TableCell align="left">{prompt.maxTokens}</TableCell>
                  <TableCell align="center">{renderActions(prompt)}</TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
